import axios from 'axios';
import { saveAs } from 'file-saver';
import { initAxios } from '../utils/axiosHelper';

/**
 * AES Analyzer client API, served by bite-server under /api/v1/aes-analyzer
 * behind keycloak.protect() and a team-membership guard. The analysis itself
 * runs on the analyzer service's workers, like the ACE and Tariff analyzers:
 * the file goes straight to S3 and the run is queued (see createRun). The
 * server returns the `{ success, data }` envelope; each method unwraps it.
 */

const getClient = () => initAxios();
const BASE = '/aes-analyzer';

export type AesSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'informational';
export type AesRunStatus =
  | 'Pending'
  | 'Running'
  | 'Completed'
  | 'Needs Review'
  | 'Failed';
export type AesCoverageStatus = 'evaluated' | 'partial' | 'not_evaluated';

/**
 * One flag on a line. `rule` is an internal id (A1, B2b, R3…) used to key
 * clears and filters; show users `flag`, never the id.
 */
export interface AesFinding {
  rule: string;
  category: string;
  flag: string;
  severity: AesSeverity;
  message: string;
  /** Cleared by the analyzer itself; only on runs from before the G1 restricted-party rule was removed. */
  autoCleared?: boolean;
  autoClearReason?: string;
}

export interface AesRuleDef {
  id: string;
  category: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'R';
  flag: string;
  severity: AesSeverity;
  title: string;
}

export interface AesRuleCoverage {
  rule: string;
  status: AesCoverageStatus;
  reason?: string;
  flagged: number;
}

export interface AesRun {
  id: string;
  name: string;
  original_file_name: string | null;
  status: AesRunStatus;
  error_message: string | null;
  line_count: number;
  shipment_count: number;
  flagged_line_count: number;
  findings_count: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  total_value: string | number | null;
  /** 1 per unique Shipment Reference Number; null until the run completes. */
  credits_charged: number | null;
  initiated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AesRunDetails extends AesRun {
  column_map: Record<string, string>;
  missing_fields: string[];
  /** Uploaded headers in file order, including columns the analyzer ignores. */
  source_headers: string[];
  /** How the parser found the data (sheet, header row, blank rows dropped). */
  upload_notes: string[];
  /** Run-level notes from the audit (e.g. routed lines missing agent-filed fields). */
  audit_notes: string[];
  coverage: AesRuleCoverage[];
  exception_usage: Record<string, number>;
  reference_version: string | null;
  rules: AesRuleDef[];
  fieldLabels: Record<string, string>;
  /** Open findings per rule id. */
  ruleCounts: Record<string, number>;
  /** User-cleared findings per rule id (what a bulk restore would reopen). */
  clearedRuleCounts: Record<string, number>;
  severityCounts: Record<AesSeverity, number>;
  tabCounts: {
    all: number;
    flagged: number;
    clean: number;
    reviewed: number;
    cleared: number;
  };
}

export interface AesClearedFlag {
  signature: string;
  clearedByUserId: string;
  clearedByName: string | null;
  clearedAt: string;
  /** Cleared together with every open finding of its rule. */
  bulk?: boolean;
}

export interface AesNote {
  text: string;
  authorId: string;
  authorName: string | null;
  createdAt: string;
}

export interface AesLine {
  id: string;
  run_id: string;
  row_number: number;
  shipment_reference: string | null;
  itn: string | null;
  line_number: string | null;
  usppi_id: string | null;
  consignee_name: string | null;
  destination_country: string | null;
  license_type: string | null;
  license_number: string | null;
  eccn: string | null;
  usml_category: string | null;
  hts: string | null;
  description: string | null;
  line_value: string | number | null;
  export_date: string | null;
  fields: Record<string, string>;
  raw_row: Record<string, string>;
  findings: AesFinding[];
  max_severity: AesSeverity | null;
  active_findings_count: number;
  is_reviewed: boolean;
  notes: AesNote[];
  cleared_flags: AesClearedFlag[];
}

export interface AesLineQuery {
  page: number;
  pageSize: number;
  tab?: string;
  search?: string;
  /** Multi-select filters; OR within a list, AND across lists. */
  severities?: string[];
  rules?: string[];
  countries?: string[];
  licenseTypes?: string[];
  /** 'Y' / 'N' from the Routed Transaction Indicator. */
  routed?: string[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
}

export interface AesFilterOptions {
  severities: string[];
  rules: string[];
  countries: string[];
  licenseTypes: string[];
  /** Empty when the upload has no Routed Transaction Indicator column. */
  routed: string[];
}

export const aesFindingSignature = (f: {
  rule: string;
  message: string;
}): string => `${f.rule}|${f.message}`;

export class AesAnalyzerAPI {
  static async getRuns(groupId: string): Promise<AesRun[]> {
    const res = await getClient().get(`${BASE}/runs`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  /**
   * Upload an AES export and queue the audit. The file is PUT straight to S3
   * with a presigned URL, so it never passes through a server, and the run
   * comes back Pending. The worker reads the file: an unreadable file or a
   * short credit balance fails the run with the reason in error_message.
   */
  static async createRun(
    groupId: string,
    name: string,
    file: File,
    spendMode: 'trial' | 'active',
  ): Promise<AesRun> {
    const client = getClient();
    const urlRes = await client.post(
      `${BASE}/runs/upload-url`,
      { fileName: file.name, fileSize: file.size },
      { params: { groupId: groupId } },
    );
    const { uploadUrl, fileKey, contentType } = urlRes.data?.data || {};
    if (!uploadUrl || !fileKey) {
      throw new Error('Could not get an upload URL for the file.');
    }
    // Bare axios: the presigned signature is the credential, and S3 rejects a
    // request that also carries an Authorization header. The Content-Type must
    // be the one the URL was signed with; the browser sets Content-Length from
    // the file, which must match the size the URL was signed for.
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType || 'application/octet-stream' },
      timeout: 0,
    });
    const res = await client.post(
      `${BASE}/runs`,
      {
        name: name,
        fileKey: fileKey,
        fileName: file.name,
        spendMode: spendMode,
      },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async getRun(runId: string, groupId: string): Promise<AesRunDetails> {
    const res = await getClient().get(`${BASE}/runs/${runId}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async getLines(
    runId: string,
    groupId: string,
    query: AesLineQuery,
  ): Promise<{ lines: AesLine[]; total: number }> {
    const csv = (v?: string[]) => (v && v.length ? v.join(',') : undefined);
    const { severities, rules, countries, licenseTypes, routed, ...rest } =
      query;
    const res = await getClient().get(`${BASE}/runs/${runId}/lines`, {
      params: {
        groupId: groupId,
        ...rest,
        severities: csv(severities),
        rules: csv(rules),
        countries: csv(countries),
        licenseTypes: csv(licenseTypes),
        routed: csv(routed),
      },
    });
    return res.data?.data ?? { lines: [], total: 0 };
  }

  static async getFilterOptions(
    runId: string,
    groupId: string,
  ): Promise<AesFilterOptions> {
    const res = await getClient().get(`${BASE}/runs/${runId}/filter-options`, {
      params: { groupId: groupId },
    });
    return {
      severities: [],
      rules: [],
      countries: [],
      licenseTypes: [],
      routed: [],
      ...(res.data?.data || {}),
    };
  }

  static async updateStatus(
    runId: string,
    groupId: string,
    status: AesRunStatus,
  ): Promise<AesRun> {
    const res = await getClient().patch(
      `${BASE}/runs/${runId}/status`,
      { status: status },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async deleteRun(runId: string, groupId: string): Promise<void> {
    await getClient().delete(`${BASE}/runs/${runId}`, {
      params: { groupId: groupId },
    });
  }

  static async reRun(runId: string, groupId: string): Promise<AesRun> {
    const res = await getClient().post(
      `${BASE}/runs/${runId}/re-run`,
      {},
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async setReviewed(
    runId: string,
    lineId: string,
    groupId: string,
    reviewed: boolean,
  ): Promise<AesLine> {
    const res = await getClient().post(
      `${BASE}/runs/${runId}/lines/${lineId}/review`,
      { reviewed: reviewed },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async addNote(
    runId: string,
    lineId: string,
    groupId: string,
    text: string,
  ): Promise<AesLine> {
    const res = await getClient().post(
      `${BASE}/runs/${runId}/lines/${lineId}/notes`,
      { text: text },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async setFindingCleared(
    runId: string,
    lineId: string,
    groupId: string,
    signature: string,
    cleared: boolean,
  ): Promise<AesLine> {
    const res = await getClient().post(
      `${BASE}/runs/${runId}/lines/${lineId}/findings/${
        cleared ? 'clear' : 'restore'
      }`,
      { signature: signature },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  /**
   * Clear every open finding of one rule on the run, or restore every one a
   * user cleared. Returns how many lines and findings changed.
   */
  static async setRuleFindingsCleared(
    runId: string,
    groupId: string,
    rule: string,
    cleared: boolean,
  ): Promise<{ lines: number; findings: number }> {
    const res = await getClient().post(
      `${BASE}/runs/${runId}/findings/${
        cleared ? 'bulk-clear' : 'bulk-restore'
      }`,
      { rule: rule },
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? { lines: 0, findings: 0 };
  }

  /** Download every line with its findings as CSV. */
  static async downloadFindingsCsv(
    runId: string,
    groupId: string,
    runName: string,
  ): Promise<void> {
    const res = await getClient().get(`${BASE}/runs/${runId}/export.csv`, {
      params: { groupId: groupId },
      responseType: 'blob',
    });
    const safeName =
      (runName || 'aes-analysis').replace(/[^a-z0-9-_ ]/gi, '').trim() ||
      'aes-analysis';
    saveAs(res.data as Blob, `${safeName}_AES_Findings.csv`);
  }
}
